import { useCallback, useState } from 'react';

// Shared hook to control feedback modal prompting behavior across activities.
// Namespaces localStorage keys by activityKey so different activities don't conflict.

const DEFAULT_PROMPT_INTERVAL = 1; // every N games
const DEFAULT_DISMISS_DAYS = 7;

const makeKey = (activityKey, key) => `${activityKey}_feedback_${key}`;

const useFeedbackPrompt = ({
    activityKey = 'activity',
    isJourney = false,
    navigate = null,
    promptInterval = DEFAULT_PROMPT_INTERVAL,
    dismissDays = DEFAULT_DISMISS_DAYS,
} = {}) => {
    const [showFeedbackModal, setShowFeedbackModal] = useState(false);

    const handleCloseFeedbackModal = useCallback(() => {
        setShowFeedbackModal(false);
        if (!isJourney && typeof navigate === 'function') {
            // small timeout to allow modal animation to finish if needed
            setTimeout(() => navigate('/home'), 50);
        }
    }, [isJourney, navigate]);

    const handleDismissFeedbackPermanently = useCallback(() => {
        try {
            localStorage.setItem(makeKey(activityKey, 'dismissed_permanently'), 'true');
            localStorage.setItem(makeKey(activityKey, 'first_completed'), 'true');
        } catch (err) {
            console.error('Failed to write feedback dismiss flag:', err);
        }
        setShowFeedbackModal(false);
        if (!isJourney && typeof navigate === 'function') {
            setTimeout(() => navigate('/home'), 50);
        }
    }, [activityKey, isJourney, navigate]);

    const showIfAppropriate = useCallback((forceShow = false) => {
        // Do not show feedback prompts in Journey mode.
        if (isJourney) return false;

        try {
            const dismissed = localStorage.getItem(makeKey(activityKey, 'dismissed_permanently')) === 'true';
            if (dismissed && !forceShow) return false;

            // Increment games played count
            const gamesKey = makeKey(activityKey, 'games_played');
            const lastPromptKey = makeKey(activityKey, 'last_prompt_time');
            const firstCompletedKey = makeKey(activityKey, 'first_completed');

            const previousCount = parseInt(localStorage.getItem(gamesKey) || '0', 10);
            const newCount = previousCount + 1;
            localStorage.setItem(gamesKey, String(newCount));

            const hasCompletedFirstTime = localStorage.getItem(firstCompletedKey) === 'true';

            if (forceShow) {
                setShowFeedbackModal(true);
                localStorage.setItem(firstCompletedKey, 'true');
                localStorage.setItem(lastPromptKey, Date.now().toString());
                return true;
            }

            if (!hasCompletedFirstTime) {
                setShowFeedbackModal(true);
                localStorage.setItem(firstCompletedKey, 'true');
                localStorage.setItem(lastPromptKey, Date.now().toString());
                return true;
            }

            const lastPrompt = parseInt(localStorage.getItem(lastPromptKey) || '0', 10);
            const now = Date.now();
            const oneDay = 24 * 60 * 60 * 1000;
            const dismissDurationMs = dismissDays * oneDay;

            const shouldShowBasedOnTime = (now - lastPrompt) > dismissDurationMs;
            const shouldShowBasedOnCount = (newCount % promptInterval === 0);

            if (shouldShowBasedOnTime || shouldShowBasedOnCount) {
                setShowFeedbackModal(true);
                localStorage.setItem(lastPromptKey, now.toString());
                return true;
            }
        } catch (err) {
            console.error('Feedback prompt check failed:', err);
            // On storage errors, show the modal to be safe
            setShowFeedbackModal(true);
            return true;
        }

        return false;
    }, [activityKey, isJourney, promptInterval, dismissDays]);

    return {
        showFeedbackModal,
        setShowFeedbackModal,
        showIfAppropriate,
        handleCloseFeedbackModal,
        handleDismissFeedbackPermanently,
    };
};

export default useFeedbackPrompt;

